import { signal } from '@preact/signals';
import { useEffect } from 'preact/hooks';

import type { CareAttention } from './CareAttention.js';
import type { CareSetupStep } from './CareSetupStep.js';
import type { CheckInWidget } from './CheckInWidget.js';
import { Skeleton } from '../lib/ui.js';

// Golf 8 (0050): de check-in-widget en de wizardstap zitten in een eigen chunk (alleen huishoudens
// met de check-in laden ze). Vandaag en de wizard importeren alleen dit kleine bestand.

const widget = signal<typeof CheckInWidget | null>(null);
let pending: Promise<void> | null = null;

function load(): Promise<void> {
  if (!pending) {
    pending = import('./CheckInWidget.js')
      .then((m) => {
        widget.value = m.CheckInWidget;
      })
      .catch(() => {
        // Netwerkfout: bij de volgende render opnieuw proberen.
        pending = null;
      });
  }
  return pending;
}

const setup = signal<typeof CareSetupStep | null>(null);

/** De wizardstap voor Zorg (§3.3.1). */
export function LazyCareSetupStep(props: { onDone: () => void; onLater: () => void }) {
  useEffect(() => {
    if (!setup.value) {
      void import('./CareSetupStep.js')
        .then((m) => (setup.value = m.CareSetupStep))
        .catch(() => undefined);
    }
  }, []);
  const Step = setup.value;
  if (!Step) return <Skeleton rows={6} />;
  return <Step mode="wizard" onDone={props.onDone} onLater={props.onLater} />;
}

export function LazyCheckInWidget(props: { tick: number; relative?: boolean }) {
  useEffect(() => {
    void load();
  }, []);
  const Widget = widget.value;
  if (!Widget) {
    return (
      <section class="panel" data-name="widget-check-in-loading">
        <Skeleton rows={2} />
      </section>
    );
  }
  return <Widget {...props} />;
}

const attention = signal<typeof CareAttention | null>(null);

/**
 * Bovenaan Vandaag (review golf 8 r2): de vragen aan de oudere zelf en "de check-in staat nog uit"
 * (eigen chunk; zonder iets te melden tekent hij niets).
 */
export function LazyCareAttention(props: { tick: number }) {
  useEffect(() => {
    if (!attention.value) {
      void import('./CareAttention.js')
        .then((m) => (attention.value = m.CareAttention))
        .catch(() => undefined);
    }
  }, []);
  const Card = attention.value;
  return Card ? <Card {...props} /> : null;
}
